// MemoryMagnets.jsx
import React, { useState, useMemo, useEffect } from 'react';
import {
  SearchIcon,
  ArrowLeft,
  ArrowRight,
  PlusIcon,
  FilterIcon
} from '../../../public/icons';
import { useNavigate } from 'react-router-dom';
import FilterModal from '../../../component/shop/FilterModal';
import Pagination from '../../../component/shared/Pagination';

const MemoryMagnets = () => {
  const navigate = useNavigate();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  const [filters, setFilters] = useState({
    availability: {
      inStock: false,
      outOfStock: false
    },
    categories: {
      travelPlaces: false,
      namesAndMeaning: false,
      loveRelationships: false,
      familyHome: false,
      cultureHeritage: false,
      faithSpiritual: false,
      legacyReflection: false,
      celebrationsMilestones: false,
      inspirationalQuotes: false
    }
  });

  const handleMagnetClick = (magnet) => {
    if (magnet.isCustom) {
      navigate('/memory-magnets/edit-template', {
        state: {
          isCustom: true,
          // 🟢 Family title — what shows on the order line
          productFamily: 'Memory Magnets',
          title: magnet.title,
          collectionTitle: null,
          description: magnet.description,
          price: 30000
        }
      });
    } else {
      navigate('/memory-magnets/other-template', {
        state: {
          // 🟢 Pass family title forward to details page
          productFamily: 'Memory Magnets',
          collectionName: magnet.title,
          collectionDescription: magnet.description
        }
      });
    }
  };

  const hasActiveFilters = () => {
    const hasAvailabilityFilters = Object.values(filters.availability).some(Boolean);
    const hasCategoryFilters = Object.values(filters.categories).some(Boolean);
    return hasAvailabilityFilters || hasCategoryFilters;
  };

  const handleFilterChange = (category, subcategory) => {
    if (category === 'availability') {
      setFilters((prev) => ({
        ...prev,
        availability: {
          ...prev.availability,
          [subcategory]: !prev.availability[subcategory]
        }
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        categories: {
          ...prev.categories,
          [subcategory]: !prev.categories[subcategory]
        }
      }));
    }
  };

  const clearFilters = () => {
    setFilters({
      availability: {
        inStock: false,
        outOfStock: false
      },
      categories: {
        travelPlaces: false,
        namesAndMeaning: false,
        loveRelationships: false,
        familyHome: false,
        cultureHeritage: false,
        faithSpiritual: false,
        legacyReflection: false,
        celebrationsMilestones: false,
        inspirationalQuotes: false
      }
    });
  };

  const memoryMagnets = [
    {
      id: 'custom',
      title: 'Create Custom Memory Magnet',
      description:
        'Upload your photo, write your story, and turn it into a magnet that holds meaning.',
      image: null,
      isCustom: true,
      category: 'custom',
      inStock: true
    },
    {
      id: 'nigeria',
      title: 'Nigeria — Travel Memory Magnet',
      description:
        'A photo-book-like collection of magnetic memories. Perfect for family history and milestones.',
      image:
        'https://afri-res.uneca.org:80/sites/default/files/styles/country_ban/public/2023-07/banner_legos.jpg?itok=yQ3txN_0',
      category: 'travelPlaces',
      inStock: true
    },
    {
      id: 'names-and-meaning',
      title: 'Names & their meaning Magnet',
      description:
        'A photo-book-like collection of magnetic memories. Perfect for family history and milestones.',
      image:
        'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      category: 'namesAndMeaning',
      inStock: true
    }
  ];

  const filteredMagnets = useMemo(() => {
    return memoryMagnets.filter((magnet) => {
      if (
        searchQuery &&
        !magnet.title.toLowerCase().includes(searchQuery.toLowerCase())
      ) {
        return false;
      }

      const availabilityFilters = filters.availability;
      if (availabilityFilters.inStock || availabilityFilters.outOfStock) {
        if (availabilityFilters.inStock && !magnet.inStock) return false;
        if (availabilityFilters.outOfStock && magnet.inStock) return false;
      }

      const categoryFilters = filters.categories;
      const hasActiveCategoryFilters = Object.values(categoryFilters).some(Boolean);
      if (hasActiveCategoryFilters) {
        return categoryFilters[magnet.category];
      }

      return true;
    });
  }, [memoryMagnets, searchQuery, filters]);

  const totalItems = filteredMagnets.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMagnets = filteredMagnets.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filters]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getHoverMessage = (magnet) => {
    if (magnet.isCustom) {
      return 'Add Your Custom Story Here';
    }

    const title = magnet.title;
    if (title.includes('Paris')) return 'Paris is a beautiful city';
    if (title.includes('Canada')) return 'Canada is a beautiful country';
    if (title.includes('Hawaii')) return 'Hawaii is a beautiful paradise';
    if (title.includes('London')) return 'London is a beautiful city';
    if (title.includes('England')) return 'England is a beautiful country';
    if (title.includes('India')) return 'India is a beautiful country';
    if (title.includes('Nigeria')) return 'Nigeria is a beautiful country';
    if (title.includes('Names & their meaning'))
      return 'Names hold beautiful stories';

    const placeName = title.split('—')[0].trim();
    return `${placeName} is beautiful`;
  };

  const getFlipBackground = () => 'bg-[#2B2A2A]';

  return (
    <div className="min-h-screen bg-[#FCF7EE] relative">
      {/* Header Section */}
      <section className="py-6 md:py-10">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl text-left">
          <div className="mb-8 md:mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#1C2A46] mb-4 md:mb-6">
              Memory Magnets
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-[#6B635D] font-normal mb-6 md:mb-8 max-w-2xl">
              Small moments. Lasting stories. Choose a ready-made memory or
              create one that's uniquely yours.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 lg:gap-4">
            <div className="w-full lg:w-1/2 mb-4 lg:mb-0">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-medium text-[#1C2A46] mb-2 md:mb-4">
                Custom & Pre-curated collections
              </h2>
              <p className="text-sm sm:text-base text-[#6B635D] max-w-3xl">
                Every Kapta piece is a vessel for memory designed to hold
                voices, moments, and meaning.
              </p>
            </div>

            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-[280px] md:w-[320px] lg:w-[355px]">
                <input
                  type="text"
                  placeholder="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="p-3 pr-12 border w-full border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-base focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <SearchIcon size={20} />
                </div>
              </div>

              <button
                onClick={() => setIsFilterOpen(true)}
                className="flex items-center justify-center gap-2 px-4 py-3 border border-[#DBDBDB] bg-transparent hover:bg-[#F8F8F8] transition-colors w-full sm:w-auto"
              >
                <FilterIcon />
                <span className="text-[#1C2A46]">Filter</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-12 md:pb-16">
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          {paginatedMagnets.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-base text-[#6B635D]">
                No memory magnets match your search or filters.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  clearFilters();
                }}
                className="mt-4 text-[#D2A146] hover:underline text-sm"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {paginatedMagnets.map((magnet) => (
                <div
                  key={magnet.id}
                  className="group cursor-pointer"
                  onClick={() => handleMagnetClick(magnet)}
                >
                  <div className="h-full flex flex-col">
                    <div className="aspect-square mb-4 md:mb-5 overflow-hidden relative">
                      <div className="flip-card w-full h-full">
                        <div className="flip-card-front w-full h-full">
                          {magnet.isCustom ? (
                            <div className="w-full h-full bg-[#1C2A46] flex items-center justify-center">
                              <PlusIcon size={50} color="white" />
                            </div>
                          ) : (
                            <img
                              src={magnet.image}
                              alt={magnet.title}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>

                        <div
                          className={`flip-card-back w-full h-full ${getFlipBackground(
                            magnet
                          )} flex items-center justify-center p-4 md:p-6`}
                        >
                          <div className="text-center px-2 md:px-4">
                            <p className="text-white text-base sm:text-lg md:text-xl lg:text-2xl font-medium leading-tight">
                              {getHoverMessage(magnet)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 text-left mb-4 md:mb-8">
                      <h3 className="text-base sm:text-lg md:text-xl font-medium text-[#1C2A46] mb-1 md:mb-2">
                        {magnet.title}
                      </h3>
                      <p className="text-sm sm:text-base text-[#6B635D] leading-relaxed">
                        {magnet.description}
                      </p>
                    </div>

                    {!magnet.inStock && (
                      <div className="mt-2 md:mt-4 text-red-600 text-xs sm:text-sm font-medium">
                        Out of Stock
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-12 md:mt-16">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </section>

      <FilterModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      <style jsx>{`
        .flip-card {
          perspective: 1000px;
          position: relative;
          width: 100%;
          height: 100%;
          cursor: pointer;
        }
        .flip-card-front,
        .flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
          transform-style: preserve-3d;
          border-radius: 2px;
        }
        .flip-card-front { transform: rotateY(0deg); }
        .flip-card-back {
          transform: rotateY(180deg);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .group:hover .flip-card-front { transform: rotateY(-180deg); }
        .group:hover .flip-card-back { transform: rotateY(0deg); }
        @media (max-width: 768px) {
          .group:active .flip-card-front { transform: rotateY(-180deg); }
          .group:active .flip-card-back { transform: rotateY(0deg); }
        }
      `}</style>
    </div>
  );
};

export default MemoryMagnets;